import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { InferRequestType } from "hono/client";
import { authClient } from "../../context/auth-client";
import client from "../../utils/api-client";

// types
type UserRouteWithId = (typeof client.users)[":id"];

type GetUserByIdRoute = UserRouteWithId["$get"];
type GetUserByIdInput = InferRequestType<GetUserByIdRoute>["param"];

type UpdateUserRoute = UserRouteWithId["$put"];
type AdminUpdateUserInput = InferRequestType<UpdateUserRoute>;

type DeleteUserRoute = UserRouteWithId["$delete"];
type DeleteUserInput = InferRequestType<DeleteUserRoute>["param"];

type UpdateProfileRoute = (typeof client.users.me)["$put"];
type UpdateProfileInput = InferRequestType<UpdateProfileRoute>["json"];

export type UsersApiRequestTypes = {
	getAll: undefined;
	getById: GetUserByIdInput;
	getCurrentUserProfile: undefined;
	updateProfile: UpdateProfileInput;
	update: AdminUpdateUserInput["param"] & AdminUpdateUserInput["json"];
	delete: DeleteUserInput;
};

export type UsersApiResponseTypes = {
	getAll: Awaited<ReturnType<typeof useGetAllUsersQuery>>["data"];
	getById: Awaited<ReturnType<typeof useGetUserByIdQuery>>["data"];
	getCurrentUserProfile: Awaited<
		ReturnType<typeof useGetCurrentUserProfileQuery>
	>["data"];
	updateProfile: Awaited<
		ReturnType<typeof useUpdateUserProfileMutation>
	>["data"];
	update: Awaited<ReturnType<typeof useUpdateUserMutation>>["data"];
	delete: Awaited<ReturnType<typeof useDeleteUserMutation>>["data"];
};

// queries
export function useGetAllUsersQuery() {
	return useQuery({
		queryKey: ["users"],
		queryFn: async () => {
			const result = await client.users.index.$get();
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
	});
}

export function useGetUserByIdQuery(param: GetUserByIdInput) {
	return useQuery({
		queryKey: ["users", param.id],
		queryFn: async () => {
			const result = await client.users[":id"].$get({ param });
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
		enabled: !!param.id,
	});
}

export function useGetCurrentUserProfileQuery() {
	const { data: session } = authClient.useSession();
	return useQuery({
		queryKey: ["users", "profile", session?.user?.id],
		queryFn: async () => {
			if (!session?.user?.id) throw new Error("No user session found");
			const result = await client.users.me.$get();
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
		enabled: !!session?.user?.id,
	});
}

// mutations
export function useUpdateUserProfileMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (json: UpdateProfileInput) => {
			const result = await client.users.me.$put({ json });
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
		onSuccess: async () => {
			await authClient.getSession();
			queryClient.invalidateQueries({ queryKey: ["users", "profile"] });
		},
	});
}

export function useUpdateUserMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (
			input: AdminUpdateUserInput["param"] & AdminUpdateUserInput["json"],
		) => {
			const result = await client.users[":id"].$put({
				param: { id: input.id },
				json: {
					name: input.name,
					phone: input.phone,
					image: input.image,
					isActive: input.isActive,
					points: input.points,
				},
			});
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
		onSuccess: async (_, variables) => {
			await authClient.getSession();
			queryClient.invalidateQueries({
				queryKey: ["users", "profile", variables.id],
			});
		},
	});
}

export function useDeleteUserMutation() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (param: DeleteUserInput) => {
			const result = await client.users[":id"].$delete({ param });
			const parsed = await result.json();
			if (!parsed.success) throw new Error(parsed.error);
			return parsed.data;
		},
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ["users"] });
			queryClient.invalidateQueries({
				queryKey: ["users", "profile", variables.id],
			});
		},
	});
}
